import type {
	API_CreateCustomerRequest,
	API_CreateCustomerResponse,
	API_ListOrganizationCustomersPaginatedResponse,
	API_ListOrganizationCustomersResponse,
	API_ProcessCSVImportRequest,
	API_ProcessCSVImportResponse,
	API_ScheduleTrialClassRequest,
	API_ScheduleTrialClassResponse,
	API_UpdateCustomerRequest,
	API_UpdateCustomerResponse,
	API_ValidateCSVImportRequest,
	API_ValidateCSVImportResponse,
	Customer,
	PaginationRequest,
} from "@pilatestime/shared-types";
import {
	queryOptions,
	useMutation,
	useQueryClient,
} from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useMutationWithToast } from "@/hooks/useMutationWithToast";
import api from "./api_client";

// API functions
export const fetchOrganizationCustomers = (
	token: string,
	organizationId: string,
	searchTerm?: string,
): Promise<API_ListOrganizationCustomersResponse> => {
	const params = searchTerm ? { search: searchTerm } : {};
	return api
		.get(`/organizations/${organizationId}/customers`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params,
		})
		.then((res) => res.data);
};

export const fetchOrganizationCustomersPaginated = (
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
): Promise<API_ListOrganizationCustomersPaginatedResponse> => {
	const params = {
		...paginationParams,
		...(searchTerm && { search: searchTerm }),
	};
	return api
		.get(`/organizations/${organizationId}/customers`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
			params,
		})
		.then((res) => res.data);
};

export const fetchCustomerById = (
	token: string,
	organizationId: string,
	customerId: string,
): Promise<Customer> =>
	api
		.get(`/organizations/${organizationId}/customers/${customerId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const createCustomer = (
	token: string,
	organizationId: string,
	data: API_CreateCustomerRequest,
): Promise<API_CreateCustomerResponse> =>
	api
		.post(`/organizations/${organizationId}/customers`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const updateCustomer = (
	token: string,
	organizationId: string,
	customerId: string,
	data: API_UpdateCustomerRequest,
): Promise<API_UpdateCustomerResponse> =>
	api
		.put(`/organizations/${organizationId}/customers/${customerId}`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const deleteCustomer = (
	token: string,
	organizationId: string,
	customerId: string,
): Promise<void> =>
	api
		.delete(`/organizations/${organizationId}/customers/${customerId}`, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const searchCustomers = (
	token: string,
	organizationId: string,
	searchTerm: string,
): Promise<API_ListOrganizationCustomersResponse> =>
	fetchOrganizationCustomers(token, organizationId, searchTerm);

// Query options
export const fetchOrganizationCustomersQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	searchTerm?: string,
) =>
	queryOptions({
		queryKey: searchTerm
			? ["organization_customers_search", organizationId, userId, searchTerm]
			: ["organization_customers", organizationId, userId],
		queryFn: () =>
			fetchOrganizationCustomers(token, organizationId, searchTerm),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchCustomerByIdQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	customerId: string,
) =>
	queryOptions({
		queryKey: ["customer", organizationId, customerId, userId],
		queryFn: () => fetchCustomerById(token, organizationId, customerId),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

export const fetchOrganizationCustomersPaginatedQueryOptions = (
	userId: string,
	token: string,
	organizationId: string,
	paginationParams: PaginationRequest,
	searchTerm?: string,
) =>
	queryOptions({
		queryKey: searchTerm
			? [
					"organization_customers_paginated_search",
					organizationId,
					userId,
					paginationParams,
					searchTerm,
				]
			: [
					"organization_customers_paginated",
					organizationId,
					userId,
					paginationParams,
				],
		queryFn: () =>
			fetchOrganizationCustomersPaginated(
				token,
				organizationId,
				paginationParams,
				searchTerm,
			),
		staleTime: 5 * 60 * 1000, // 5 minutes
	});

// Mutation hooks
export const useCreateCustomer = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			data,
		}: {
			token: string;
			organizationId: string;
			data: API_CreateCustomerRequest;
		}) => createCustomer(token, organizationId, data),
		toastMessages: {
			loading: t("customers.toasts.creating"),
			success: t("customers.toasts.createSuccess"),
			error: t("customers.toasts.createError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization customers
			queryClient.invalidateQueries({
				queryKey: ["organization_customers", variables.organizationId],
			});
			// Also invalidate search queries
			queryClient.invalidateQueries({
				queryKey: ["organization_customers_search", variables.organizationId],
			});
			// Invalidate paginated queries
			queryClient.invalidateQueries({
				queryKey: [
					"organization_customers_paginated",
					variables.organizationId,
				],
			});
			// Invalidate paginated search queries
			queryClient.invalidateQueries({
				queryKey: [
					"organization_customers_paginated_search",
					variables.organizationId,
				],
			});
		},
	});
};

export const useUpdateCustomer = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			customerId,
			data,
		}: {
			token: string;
			organizationId: string;
			customerId: string;
			data: API_UpdateCustomerRequest;
		}) => updateCustomer(token, organizationId, customerId, data),
		toastMessages: {
			loading: t("customers.toasts.updating"),
			success: t("customers.toasts.updateSuccess"),
			error: t("customers.toasts.updateError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization customers
			queryClient.invalidateQueries({
				queryKey: ["organization_customers", variables.organizationId],
			});
			// Also invalidate search queries
			queryClient.invalidateQueries({
				queryKey: ["organization_customers_search", variables.organizationId],
			});
			// Invalidate paginated queries
			queryClient.invalidateQueries({
				queryKey: [
					"organization_customers_paginated",
					variables.organizationId,
				],
			});
			// Invalidate paginated search queries
			queryClient.invalidateQueries({
				queryKey: [
					"organization_customers_paginated_search",
					variables.organizationId,
				],
			});
			// Invalidate specific customer query
			queryClient.invalidateQueries({
				queryKey: ["customer", variables.organizationId, variables.customerId],
			});
		},
	});
};

export const useDeleteCustomer = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			customerId,
		}: {
			token: string;
			organizationId: string;
			customerId: string;
		}) => deleteCustomer(token, organizationId, customerId),
		toastMessages: {
			loading: t("customers.toasts.deleting"),
			success: t("customers.toasts.deleteSuccess"),
			error: t("customers.toasts.deleteError"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate and refetch organization customers
			queryClient.invalidateQueries({
				queryKey: ["organization_customers", variables.organizationId],
			});
			// Also invalidate search queries
			queryClient.invalidateQueries({
				queryKey: ["organization_customers_search", variables.organizationId],
			});
			// Invalidate paginated queries
			queryClient.invalidateQueries({
				queryKey: [
					"organization_customers_paginated",
					variables.organizationId,
				],
			});
			// Invalidate paginated search queries
			queryClient.invalidateQueries({
				queryKey: [
					"organization_customers_paginated_search",
					variables.organizationId,
				],
			});
			// Remove specific customer from cache
			queryClient.removeQueries({
				queryKey: ["customer", variables.organizationId, variables.customerId],
			});
			// Invalidate attendance queries that might include this customer
			queryClient.invalidateQueries({
				queryKey: ["attendance"],
			});
			// Invalidate medical records for this customer
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records",
					variables.organizationId,
					variables.customerId,
				],
			});
			queryClient.invalidateQueries({
				queryKey: [
					"customer_medical_records_paginated",
					variables.organizationId,
					variables.customerId,
				],
			});
		},
	});
};

export const useSearchCustomers = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: ({
			token,
			organizationId,
			searchTerm,
		}: {
			token: string;
			organizationId: string;
			searchTerm: string;
		}) => searchCustomers(token, organizationId, searchTerm),
		onSuccess: (data, variables) => {
			// Update the search query cache
			queryClient.setQueryData(
				[
					"organization_customers_search",
					variables.organizationId,
					variables.searchTerm,
				],
				data,
			);
		},
	});
};

// CSV Import API functions
export const validateCSVImport = (
	token: string,
	organizationId: string,
	data: API_ValidateCSVImportRequest,
): Promise<API_ValidateCSVImportResponse> =>
	api
		.post(`/organizations/${organizationId}/customers/import/validate`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

export const processCSVImport = (
	token: string,
	organizationId: string,
	data: API_ProcessCSVImportRequest,
): Promise<API_ProcessCSVImportResponse> =>
	api
		.post(`/organizations/${organizationId}/customers/import`, data, {
			headers: {
				Authorization: `Bearer ${token}`,
			},
		})
		.then((res) => res.data);

// Trial Class API functions
export const scheduleTrialClass = (
	token: string,
	organizationId: string,
	customerId: string,
	data: API_ScheduleTrialClassRequest,
): Promise<API_ScheduleTrialClassResponse> =>
	api
		.post(
			`/organizations/${organizationId}/customers/${customerId}/trial-class`,
			data,
			{
				headers: {
					Authorization: `Bearer ${token}`,
				},
			},
		)
		.then((res) => res.data);

export const useScheduleTrialClass = () => {
	const queryClient = useQueryClient();
	const { t } = useTranslation();

	return useMutationWithToast({
		mutationFn: ({
			token,
			organizationId,
			customerId,
			data,
		}: {
			token: string;
			organizationId: string;
			customerId: string;
			data: API_ScheduleTrialClassRequest;
		}) => scheduleTrialClass(token, organizationId, customerId, data),
		toastMessages: {
			loading: t("customers.singleSession.toasts.scheduling"),
			success: t("customers.singleSession.toasts.success"),
			error: t("customers.singleSession.toasts.error"),
		},
		onSuccess: (_data, variables) => {
			// Invalidate attendance queries
			queryClient.invalidateQueries({
				queryKey: ["attendance"],
				refetchType: "all",
			});
			// Invalidate schedule sessions (matches scheduleSessionKeys.all)
			// Use refetchType: "all" to ensure inactive queries are also refetched when they become active
			queryClient.invalidateQueries({
				queryKey: ["scheduleSessions"],
				refetchType: "all",
			});
			// Invalidate customer data
			queryClient.invalidateQueries({
				queryKey: ["customer", variables.organizationId, variables.customerId],
			});
		},
	});
};
